import differenceInMinutes from 'date-fns/differenceInMinutes';
import {Language} from "../config/LanguageConfig";
import {DateRange} from "../models/common/DateRange";
import {startOfMonth, lastDayOfMonth, startOfWeek, endOfWeek, startOfYear, lastDayOfYear} from 'date-fns';
import {Dateable, TimePeriodable} from "../models/common/TimePeriodable";
import {Nullable} from "../models/common/Nullable";


/**
 * Returns the name of the month in english in lower case.
 * These names can be used to index the language variable months to get the translated version of the name
 *
 * @param month
 */
export function getMonthName( month: number ): string {
    const monthNames = [
        "january", "february", "march", "april", "may", "june",
        "july", "august", "september", "october", "november", "december"
    ];


    return monthNames[month];

}


export function decimalToMinutes( value: number ) {
    const hour = Math.trunc( value );

    if(hour === value ) {
        return Math.round( hour * 60 );
    } else {
        let minute = Math.round( ( value - hour ) * 60 );
        minute = ((minute < 0) ? minute*-1 : minute);

        return hour * 60  + minute;
    }
}

/**
 * converts decimal number to timeformat hour:minute
 *
 * @param num number
 * @param formatter formatting function to get the time format you want
 * @return string
 */
export const decimalToHM = (num: number, formatter?: ( hours: number, minutes: number ) => string ) => {

    const hour = Math.trunc( num );

    if( hour === num ) {

        if( typeof formatter !== 'undefined') {
            return  formatter( hour, 0 );
        }

        return hour.toString().padStart( 2, '0') + ':00';
    } else {
        let minute = Math.round( ( num - hour ) * 60 );
        minute = ((minute < 0) ? minute * -1 : minute);

        if( typeof formatter !== 'undefined') {
            return  formatter( hour, minute );
        }


        return hour + ':' + ((minute < 10 ) ? '0'+minute : minute );
    }
};

/**
 * standard date formatting function
 */
export const formatDate = ( date: Nullable< Dateable >, language: Language ) => {
    if( date === null ) {
        return '';
    }
    const options = { year: '2-digit', month: '2-digit', day: '2-digit' } as const;

    if( typeof date === 'number') {
        return (new Date( date )).toLocaleDateString( language.locale, options );
    } else {
        return date.toLocaleDateString(language.locale, options);
    }

};

/**
 * extracts the time part form the date object and returns it as string formatted by toLocaleDateString
 *
 *
 * @param date
 * @param language
 */
export function formatTime( date: Dateable, language: Language ) {
    const timeOptions = { hour: '2-digit', minute: '2-digit' } as const;

    if( typeof date === 'number') {
        const newDate = new Date(date);
        return newDate.toLocaleTimeString( language.locale, timeOptions );
    } else {
        return date.toLocaleTimeString( language.locale, timeOptions );
    }
}

/**
 * standard date time formatting function
 * time is included
 */
export const formatDateTime = ( date: Nullable< Dateable >, language: Language ) => {
    if( date === null ) {
        return '';
    }

    const dateOptions = { year: '2-digit', month: '2-digit', day: '2-digit' } as const;
    const timeOptions = { hour: '2-digit', minute: '2-digit' } as const;

    if( typeof date === 'number') {
        const newDate = new Date(date);
        return newDate.toLocaleDateString( language.locale, dateOptions ) + " "+ newDate.toLocaleTimeString( language.locale, timeOptions );
    } else {
        return date.toLocaleDateString(language.locale, dateOptions)+ " "+ date.toLocaleTimeString( language.locale, timeOptions );
    }

};

/**
 * formats a date to a string which can be inserted into a date fieldtype mysql/mariadb
 *
 * @param date
 */
export const formatDbDate = ( date: Dateable) => {
    const newDate = new Date( date );
    return newDate.getFullYear() +'-' + newDate.getMonth() +'-'+newDate.getDate()
}

/**
 * get a DateTimeRange object for the week from the given date
 *
 * starts monday = 1
 *
 * @param date
 */
export function getWeekRange( date: Date ): DateRange {

    return {
        start: startOfWeek( date, { weekStartsOn: 1 } ),
        endInclusive: endOfWeek( date, { weekStartsOn: 1 } )
    }

}

export function getCurrentWeekRange(): DateRange {
    return getWeekRange( new Date() );
}

/**
 * checks the given date, if it is already a number ( timestamp ), otherwise calls Date::getTime() on param.
 * @param date { number | Date }
 */
export function dateAsTimestamp( date: Dateable  ): number {
    return ( typeof date === "number" ) ? date : date.getTime();
}

/**
 * wrapper function around dateAsTimestamp to allow nullable dates
 * @param date
 */
export function nullableDateAsTimestamp( date: Nullable<Dateable> ): Nullable<number> {
    if( date === null ) return null;

    return dateAsTimestamp( date );
}

/**
 *  get DateTimeRange object for month from the given date
 *
 * @param date
 */
export function getMonthRange( date: Date ): DateRange {

    return {
        start: startOfMonth( date ),
        endInclusive: lastDayOfMonth( date )
    };
}

export function getYearRange( date: Date ): DateRange {
    return {
        start: startOfYear( date ),
        endInclusive: lastDayOfYear( date )
    };
}

/**
 * divides the given minutes by 60 to get the decimal hour value and returns it
 * @param minutes
 */
export function minutesToDecimalHours( minutes: number ): number {
    return minutes / 60;
}

/**
 * returns the day of the week by german standards
 *
 * Monday = 1
 * Tuesday = 2
 * Wednesday = 3
 * Thursday = 4
 * Friday = 5
 * Saturday = 6
 * Sunday = 7
 */
export function dayOfWeek( date: Date ): number {
    return ( date.getDay() === 0 ) ? 7 : date.getDay();
}

/**
 * returns the number of hours between two dates.
 * the returned hours are decimal values and thus may included partial hour
 *
 * @param first Date
 * @param second Date
 */
export function differenceInDecimalHours( first: Date, second: Date ): number {
    return ( differenceInMinutes( second, first )  / 60.0000  );
}

/**
 * returns the number of hours ( as decimal value ) the employee has to be on break to comply with working laws in germany
 *
 * @param hours
 */
export function getMandatoryBreaktime( hours: number ): number {
    if( hours < 6 ) {
        return 0;
    } else if( hours >= 6 && hours < 9 ) {
        return .5;
    } else {
        return .75;
    }
}

/**
 * returns the date part of date object, time is set to 0:0:0
 * @param date
 */
export function dateOnly( date: Date ): Date {
    return new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        0,
        0,
        0
    );
}

/**
 * merges two dates and creates a new date instance, which is returned
 *
 * @param datePart the date( day.month.year ) of the new datetime
 * @param timePart the time ( hours:minutes:seconds ) of the new datetime
 */
export function mergeDateAndTime( datePart: Date, timePart: Date ): Date {
    return new Date(
        datePart.getFullYear(),
        datePart.getMonth(),
        datePart.getDate(),
        timePart.getHours(),
        timePart.getMinutes(),
        timePart.getSeconds()
    );

}

export function datePeriodString(timePeriod: TimePeriodable<Nullable<Dateable>, Nullable< Dateable >>, language: Language ): string {

    if( timePeriod.startAt === null && timePeriod.finishAt === null ) {
        return "";
    }

    return  formatDate( timePeriod.startAt, language ) +' - '+ formatDate( timePeriod.finishAt, language )
}
